import React, { useState } from 'react';
import Breadcrumbs from '../../components/ui/Breadcrumbs.tsx';
import { ApiConnectionCredentials, EMPTY_API_CREDENTIALS } from './apiCredentials.types.ts';
import { Settings, Database, Play, Copy, Check, Info, FileCode, KeyRound, Pencil } from 'lucide-react';

interface ApiEndpoint {
  id: string;
  name: string;
  method: 'GET' | 'POST';
  path: string;
  description: string;
  defaultPayload?: string;
  mockResponse: string;
}

const S4HANA_ENDPOINTS: ApiEndpoint[] = [
  {
    id: 'bp_get',
    name: 'Obtener Business Partners',
    method: 'GET',
    path: '/sap/opu/odata/sap/API_BUSINESS_PARTNER/A_BusinessPartner?$top=5',
    description: 'Recupera los primeros 5 Business Partners creados en el sistema ERP.',
    mockResponse: JSON.stringify({
      d: {
        results: [
          {
            BusinessPartner: "1000021",
            Customer: "1000021",
            BusinessPartnerCategory: "2",
            BusinessPartnerFullName: "Distribuidora Industrial S.A.C.",
            BusinessPartnerGrouping: "BP02",
            FirstName: "",
            LastName: "",
            CreationDate: "/Date(1718841600000)/",
            LastChangeDate: "/Date(1718841600000)/"
          },
          {
            BusinessPartner: "1000022",
            Customer: "",
            BusinessPartnerCategory: "1",
            BusinessPartnerFullName: "Juan Pérez",
            BusinessPartnerGrouping: "BP01",
            FirstName: "Juan",
            LastName: "Pérez",
            CreationDate: "/Date(1718928000000)/",
            LastChangeDate: "/Date(1718928000000)/"
          }
        ]
      }
    }, null, 2)
  },
  {
    id: 'bp_post',
    name: 'Crear Business Partner',
    method: 'POST',
    path: '/sap/opu/odata/sap/API_BUSINESS_PARTNER/A_BusinessPartner',
    description: 'Crea un nuevo Business Partner (Organización o Persona) en S/4HANA.',
    defaultPayload: JSON.stringify({
      BusinessPartnerCategory: "2",
      BusinessPartnerFullName: "Tecnologías Globales del Sur",
      BusinessPartnerGrouping: "BP02",
      OrganizationBPName1: "Tecnologías Globales del Sur",
      SearchTerm1: "TGS",
      to_BusinessPartnerAddress: {
        results: [
          {
            Country: "PE",
            CityName: "Lima",
            PostalCode: "15046",
            StreetName: "Av. Javier Prado Este"
          }
        ]
      }
    }, null, 2),
    mockResponse: JSON.stringify({
      d: {
        BusinessPartner: "1000045",
        Customer: "1000045",
        BusinessPartnerCategory: "2",
        BusinessPartnerFullName: "Tecnologías Globales del Sur",
        BusinessPartnerGrouping: "BP02",
        CreationDate: "/Date(1719014400000)/",
        to_BusinessPartnerAddress: {
          results: [
            {
              BusinessPartner: "1000045",
              AddressID: "20448",
              Country: "PE",
              CityName: "Lima",
              StreetName: "Av. Javier Prado Este"
            }
          ]
        }
      }
    }, null, 2)
  },
  {
    id: 'po_get',
    name: 'Obtener Órdenes de Compra',
    method: 'GET',
    path: '/sap/opu/odata/sap/API_PURCHASEORDER_PROCESS_SRV/A_PurchaseOrder?$top=3',
    description: 'Lista los documentos de orden de compra (Headers) registrados.',
    mockResponse: JSON.stringify({
      d: {
        results: [
          {
            PurchaseOrder: "4500000120",
            CompanyCode: "1010",
            PurchaseOrderType: "NB",
            PurchasingOrganization: "1010",
            PurchasingGroup: "001",
            Supplier: "1000021",
            DocumentDate: "/Date(1718841600000)/",
            CreationDate: "/Date(1718841600000)/"
          },
          {
            PurchaseOrder: "4500000121",
            CompanyCode: "1010",
            PurchaseOrderType: "NB",
            PurchasingOrganization: "1010",
            PurchasingGroup: "002",
            Supplier: "1000022",
            DocumentDate: "/Date(1718928000000)/",
            CreationDate: "/Date(1718928000000)/"
          }
        ]
      }
    }, null, 2)
  },
  {
    id: 'po_post',
    name: 'Crear Orden de Compra',
    method: 'POST',
    path: '/sap/opu/odata/sap/API_PURCHASEORDER_PROCESS_SRV/A_PurchaseOrder',
    description: 'Crea una orden de compra en estado borrador o lista para aprobación.',
    defaultPayload: JSON.stringify({
      CompanyCode: "1010",
      PurchaseOrderType: "NB",
      Supplier: "1000021",
      Language: "ES",
      PurchasingOrganization: "1010",
      PurchasingGroup: "001",
      to_PurchaseOrderItem: [
        {
          PurchaseOrderItem: "10",
          Material: "MAT_RAW_01",
          Plant: "1010",
          OrderQuantity: "150.00",
          PurchaseOrderQuantityUnit: "PC",
          NetPriceAmount: "12.50",
          DocumentCurrency: "USD"
        }
      ]
    }, null, 2),
    mockResponse: JSON.stringify({
      d: {
        PurchaseOrder: "4500000204",
        CompanyCode: "1010",
        PurchaseOrderType: "NB",
        Supplier: "1000021",
        CreationDate: "/Date(1719014400000)/",
        to_PurchaseOrderItem: {
          results: [
            {
              PurchaseOrder: "4500000204",
              PurchaseOrderItem: "10",
              Material: "MAT_RAW_01",
              OrderQuantity: "150.00",
              NetPriceAmount: "12.50",
              Plant: "1010"
            }
          ]
        }
      }
    }, null, 2)
  }
];

interface S4HanaApisProps {
  setPage: (page: string) => void;
  embedded?: boolean;
  credentials?: ApiConnectionCredentials;
}

export default function S4HanaApis({ setPage, embedded = false, credentials = EMPTY_API_CREDENTIALS }: S4HanaApisProps) {
  const [selectedEndpoint, setSelectedEndpoint] = useState<ApiEndpoint>(S4HANA_ENDPOINTS[0]);
  const [payload, setPayload] = useState<string>(selectedEndpoint.defaultPayload || '');
  const [response, setResponse] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<'params' | 'headers'>('params');

  const handleEndpointSelect = (ep: ApiEndpoint) => {
    setSelectedEndpoint(ep);
    setPayload(ep.defaultPayload || '');
    setResponse('');
  };

  const handleCopyResponse = () => {
    navigator.clipboard.writeText(response);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSendRequest = () => {
    setLoading(true);
    setResponse('');
    setTimeout(() => {
      setResponse(selectedEndpoint.mockResponse);
      setLoading(false);
    }, 1200);
  };

  return (
    <div className={embedded ? 'text-white' : 'min-h-screen p-4 md:p-8 text-white bg-transparent relative overflow-hidden'}>
      {!embedded && (
        <>
          <div className="absolute top-[-10%] left-[-5%] w-[500px] h-[500px] bg-indigo-900/20 rounded-full blur-[120px] pointer-events-none z-0" />
          <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-purple-900/10 rounded-full blur-[100px] pointer-events-none z-0" />
        </>
      )}

      <div className={embedded ? 'flex flex-col h-full' : 'relative z-10 max-w-7xl mx-auto flex flex-col h-full'}>
        {!embedded && <Breadcrumbs currentPage="s4-apis" setPage={setPage} />}

        <header className="mb-8 mt-6">
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <span className="p-2 bg-indigo-500/20 rounded-xl text-indigo-400">
              <Database className="w-8 h-8" />
            </span>
            SAP S/4HANA Cloud API Explorer
          </h1>
          <p className="text-gray-400 text-sm font-light mt-1 max-w-2xl">
            Prueba de servicios OData v2 expuestos en el ERP SAP S/4HANA Cloud. Configura cabeceras y payloads para simular integraciones en tiempo real.
          </p>
        </header>

        <div className="mb-8 flex flex-wrap items-center gap-x-6 gap-y-2 bg-white/5 border border-white/10 rounded-2xl px-5 py-3.5 text-xs">
          <span className={`flex items-center gap-1.5 font-bold uppercase tracking-wider ${credentials.clientId ? 'text-emerald-400' : 'text-amber-400'}`}>
            <KeyRound className="w-3.5 h-3.5" /> {credentials.clientId ? 'Credenciales activas' : 'Sin credenciales configuradas'}
          </span>
          {credentials.clientId && (
            <>
              <span className="text-gray-500">Client ID: <span className="text-white font-mono">{credentials.clientId}</span></span>
              <span className="text-gray-500">Data Center: <span className="text-white font-mono">{credentials.dataCenter || '-'}</span></span>
            </>
          )}
          <button
            onClick={() => setPage('pruebas-apis')}
            className="ml-auto flex items-center gap-1.5 text-indigo-400 hover:text-indigo-300 font-semibold transition-colors"
          >
            <Pencil className="w-3.5 h-3.5" /> Editar credenciales
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
          {/* SIDEBAR CON SERVICIOS */}
          <aside className="lg:col-span-3 bg-white/5 backdrop-blur-xl border border-white/10 rounded-[2rem] p-6 flex flex-col gap-2">
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4 px-3">
              Endpoints SAP
            </h3>
            <div className="space-y-1.5 overflow-y-auto max-h-[500px]">
              {S4HANA_ENDPOINTS.map((ep) => (
                <button
                  key={ep.id}
                  onClick={() => handleEndpointSelect(ep)}
                  className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-300 flex flex-col gap-1 ${
                    selectedEndpoint.id === ep.id
                      ? 'bg-indigo-600/90 text-white shadow-lg'
                      : 'text-gray-400 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-[9px] font-extrabold px-1.5 py-0.5 rounded ${
                        ep.method === 'GET' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-blue-500/20 text-blue-400'
                      }`}
                    >
                      {ep.method}
                    </span>
                    <span className="font-semibold text-xs truncate">{ep.name}</span>
                  </div>
                </button>
              ))}
            </div>
          </aside>

          {/* REQUEST BUILDER */}
          <section className="lg:col-span-5 bg-white/5 backdrop-blur-xl border border-white/10 rounded-[2.5rem] p-8 flex flex-col justify-between">
            <div className="space-y-6">
              {/* Info del Endpoint */}
              <div>
                <h3 className="text-xl font-bold flex items-center gap-2">
                  <Settings className="w-5 h-5 text-indigo-400" /> Configurar Petición
                </h3>
                <p className="text-gray-400 text-xs mt-1 leading-relaxed">
                  {selectedEndpoint.description}
                </p>
              </div>

              {/* URL/Path Bar */}
              <div className="bg-black/30 border border-white/10 rounded-xl p-3.5 flex items-center gap-2.5 font-mono text-xs overflow-hidden">
                <span
                  className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                    selectedEndpoint.method === 'GET'
                      ? 'bg-emerald-500/20 text-emerald-400'
                      : 'bg-blue-500/20 text-blue-400'
                  }`}
                >
                  {selectedEndpoint.method}
                </span>
                <span className="text-gray-300 truncate w-full" title={selectedEndpoint.path}>
                  {selectedEndpoint.path}
                </span>
              </div>

              {/* Tabs para Payload / Headers */}
              <div>
                <div className="flex border-b border-white/10 mb-4 text-xs font-semibold">
                  <button
                    onClick={() => setActiveTab('params')}
                    className={`pb-2.5 px-4 relative ${
                      activeTab === 'params' ? 'text-indigo-400 border-b-2 border-indigo-400' : 'text-gray-500 hover:text-white'
                    }`}
                  >
                    Payload JSON
                  </button>
                  <button
                    onClick={() => setActiveTab('headers')}
                    className={`pb-2.5 px-4 relative ${
                      activeTab === 'headers' ? 'text-indigo-400 border-b-2 border-indigo-400' : 'text-gray-500 hover:text-white'
                    }`}
                  >
                    Cabeceras
                  </button>
                </div>

                {activeTab === 'params' ? (
                  <div>
                    {selectedEndpoint.method === 'POST' ? (
                      <textarea
                        className="w-full h-72 font-mono text-xs bg-black/40 border border-white/10 rounded-xl p-4 text-indigo-200 outline-none focus:border-indigo-500 resize-none"
                        value={payload}
                        onChange={(e) => setPayload(e.target.value)}
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center text-center h-72 border border-white/5 rounded-xl bg-black/10 text-gray-500">
                        <Info className="w-8 h-8 text-gray-600 mb-2" />
                        <p className="text-xs">Los métodos GET no requieren cuerpo de envío.</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2 h-72 overflow-y-auto pr-2">
                    <div className="flex justify-between items-center bg-black/20 p-3 rounded-lg border border-white/5 font-mono text-xs">
                      <span className="text-gray-400">Content-Type</span>
                      <span className="text-indigo-300">application/json</span>
                    </div>
                    <div className="flex justify-between items-center bg-black/20 p-3 rounded-lg border border-white/5 font-mono text-xs">
                      <span className="text-gray-400">Accept</span>
                      <span className="text-indigo-300">application/json</span>
                    </div>
                    <div className="flex justify-between items-center bg-black/20 p-3 rounded-lg border border-white/5 font-mono text-xs">
                      <span className="text-gray-400">SAP-Client</span>
                      <span className="text-indigo-300">100</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Ejecutar */}
            <button
              onClick={handleSendRequest}
              disabled={loading}
              className="mt-6 w-full py-4 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 disabled:opacity-50 rounded-xl font-bold flex items-center justify-center gap-2.5 transition-all transform active:scale-[0.98]"
            >
              <Play className="w-4 h-4 fill-white" />
              {loading ? 'Procesando...' : 'Enviar Petición'}
            </button>
          </section>

          {/* RESPONSE VIEWER */}
          <section className="lg:col-span-4 bg-white/5 backdrop-blur-xl border border-white/10 rounded-[2.5rem] p-8 flex flex-col justify-between">
            <div className="flex-1 flex flex-col">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold flex items-center gap-2">
                  <FileCode className="w-5 h-5 text-purple-400" /> Consola de Salida
                </h3>
                {response && (
                  <button
                    onClick={handleCopyResponse}
                    className="p-2 hover:bg-white/10 rounded-lg text-gray-400 hover:text-white transition-colors"
                    title="Copiar respuesta"
                  >
                    {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                  </button>
                )}
              </div>

              {/* Consola */}
              <div className="flex-1 bg-black/50 border border-white/10 rounded-2xl p-4 font-mono text-[11px] leading-normal overflow-auto min-h-[300px] max-h-[360px] custom-scrollbar text-emerald-400">
                {loading ? (
                  <span className="text-gray-500 animate-pulse block">Conectando con SAP S/4HANA Cloud...</span>
                ) : response ? (
                  <pre className="whitespace-pre-wrap">{response}</pre>
                ) : (
                  <span className="text-gray-600 italic block">Envía una petición para visualizar la respuesta del servidor...</span>
                )}
              </div>
            </div>

            {/* Status Info */}
            {response && !loading && (
              <div className="mt-6 flex justify-between items-center text-xs border-t border-white/10 pt-4 text-gray-500">
                <span>Status: <strong className="text-emerald-400 font-bold">200 OK</strong></span>
                <span>Time: <strong className="text-white">124 ms</strong></span>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
